import * as RadioGroup from '@radix-ui/react-radio-group';
import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';

export interface SegmentedOption<V extends string = string> {
  value: V;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
  description?: string;
}

/** Site-wide segmented control. Every option renders with the same box, so
 * swapping the active state never shifts row height or content below it. */
export function Segmented<V extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  compact = true,
  fullWidth = false,
  className,
}: Readonly<{
  value: V;
  onChange(value: V): void;
  options: ReadonlyArray<SegmentedOption<V>>;
  ariaLabel?: string;
  compact?: boolean;
  fullWidth?: boolean;
  className?: string;
}>) {
  return (
    <RadioGroup.Root
      data-slot='segmented'
      orientation='horizontal'
      value={value}
      onValueChange={next => onChange(next as V)}
      aria-label={ariaLabel}
      className={cn('inline-flex items-center gap-1 rounded-lg bg-muted p-1', fullWidth && 'w-full', className)}
    >
      {options.map((option) => {
        return (
          <RadioGroup.Item
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            title={option.description}
            className={cn(
              'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 text-sm font-medium text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/30 disabled:pointer-events-none disabled:opacity-50 data-[state=checked]:bg-background data-[state=checked]:text-foreground data-[state=checked]:shadow-sm',
              compact ? 'h-7 text-xs' : 'h-8',
              fullWidth && 'flex-1',
            )}
          >
            {option.icon ? <span className='inline-flex items-center [&_svg]:size-3.5'>{option.icon}</span> : null}
            <span>{option.label}</span>
          </RadioGroup.Item>
        );
      })}
    </RadioGroup.Root>
  );
}
